<main class="flex justify-content-center">
  <div class="content">
    <qa-pinned-validations />

    <div class="text-center w-12 my-5">
      <h1>My validations</h1>
      <p>Here you can view, filter, and manage all your validation runs.</p>

      @if (totalRecords$ | async; as total) {
        <div>{{ total }} validation runs found.</div>
      } @else {
        <div>0 validation runs found.</div>
      }
    </div>

    <form [formGroup]="filterForm" class="flex flex-row flex-wrap gap-3 my-5">
      <div class="flex flex-column">
        <p-floatLabel>
          <p-date-picker
            id="dateFilter"
            selectionMode="range"
            dateFormat="yy-mm-dd"
            formControlName="dateRange"
            [showIcon]="true"
          ></p-date-picker>
          <label for="dateFilter">Choose date range</label>
        </p-floatLabel>
      </div>
      <div class="flex flex-column">
        <p-floatLabel>
          <input
            id="nameFilter"
            type="text"
            pInputText
            formControlName="name"
          />
          <label for="nameFilter">Search by name</label>
        </p-floatLabel>
      </div>
      <div class="flex flex-column">
        <p-floatLabel>
          <input
            id="datasetFilter"
            type="text"
            pInputText
            formControlName="dataset"
          />
          <label for="datasetFilter">Search by dataset</label>
        </p-floatLabel>
      </div>
    </form>
    @if (selectedRows.length) {
      <div class="bulk-bar flex align-items-center flex-wrap gap-2 my-3 p-2">
        <span class="font-semibold">{{ selectedRows.length }} selected</span>

        <button pButton type="button" icon="pi pi-trash"
                [label]="'Delete (' + deletable.length + ')'"
                severity="danger"
                [text]="true"
                size="small"
                [disabled]="!deletable.length"
                (click)="openBulkDialog('delete')"></button>

        <button pButton type="button" icon="pi pi-box"
        [label]="'Archive (' + archivable.length + ')'"
        class="p-button-text p-button-sm"
        [disabled]="!archivable.length"
        (click)="openBulkDialog('archive')">
        </button>

        <button pButton type="button" icon="pi pi-calendar"
                [label]="'Un-archive (' + unarchivable.length + ')'"
                class="p-button-text p-button-sm"
                [disabled]="!unarchivable.length"
                (click)="openBulkDialog('unarchive')"></button>

        <button pButton type="button" label="Clear" icon="pi pi-times"
                class="p-button-text p-button-sm p-button-secondary"
                (click)="selectedRows = []"></button>
      </div>
    }

    <p-table
      [value]="rows$ | async"
      dataKey="id"
      [(selection)]="selectedRows"
      [rowSelectable]="isRowSelectable"
      [paginator]="true"
      [rows]="PAGE_SIZE"
      [lazy]="true"
      [loading]="loading$ | async"
      [totalRecords]="(totalRecords$ | async) || 0"
      (onPage)="onPageChange($event)"
      (onSort)="onSortChange($event)"
      sortMode="single"
      [sortField]="'start_time'"
      [sortOrder]="-1"
      [styleClass]="'p-datatable-sm p-datatable-striped qa-validations-table'"
    >
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="8" class="text-center p-5">
            <div class="flex flex-column align-items-center gap-2">
              <i
                class="pi pi-search-plus"
                style="font-size: 2rem; color: var(--text-color-secondary)"
              ></i>
              <span class="font-semibold text-xl">No validations found</span>
              <p class="muted">
                Try adjusting your filters or start a new validation.
              </p>
            </div>
          </td>
        </tr>
      </ng-template>

      <ng-template pTemplate="header">
        <tr>
          <th style="width: 3rem">
            <p-tableHeaderCheckbox></p-tableHeaderCheckbox>
          </th>
          <th pSortableColumn="start_time">
            Date <p-sortIcon field="start_time"></p-sortIcon>
          </th>
          <th pSortableColumn="name_tag">
            Name <p-sortIcon field="name_tag"></p-sortIcon>
          </th>
          <th>Datasets</th>
          <th pSortableColumn="spatial_reference_dataset">
            Spatial reference
            <p-sortIcon field="spatial_reference_dataset"></p-sortIcon>
          </th>
          <th>Load settings</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </ng-template>

      <ng-template pTemplate="body" let-rowData let-rowIndex="rowIndex">
        <tr
          class="clickable-row"
          (click)="ViewResults(rowData, $event)"
          tabindex="0"
          (keydown.enter)="ViewResults(rowData, $event)"
          [pTooltip]="'View results'"
          tooltipPosition="top"
        >
          <td class="checkbox-cell" (click)="$event.stopPropagation()">
            <p-tableCheckbox
              [value]="rowData"
              [index]="rowIndex"
              [disabled]="!isSelectable(rowData)"
            ></p-tableCheckbox>
          </td>

          <td class="date-cell">
            {{ rowData.start_time | date: "yyyy-MM-dd" }}
          </td>

          <td class="name-cell">
            <div class="flex align-items-center justify-content-start w-full">
              @if (editingRowId === rowData.id) {
                <div
                  class="flex align-items-center gap-1 w-full edit-container"
                >
                  <input
                    #editInput
                    pInputText
                    maxlength="80"
                    [(ngModel)]="tempName"
                    class="p-inputtext-sm flex-grow-1"
                    (click)="$event.stopPropagation()"
                    (keydown)="$event.stopPropagation()"
                    (keyup.enter)="saveEdit(rowData)"
                    (keyup.escape)="cancelEdit()"
                    autoFocus
                  />

                  <button
                    pButton
                    icon="pi pi-check"
                    class="p-button-text p-button-sm p-button-success p-0 action-btn"
                    (mousedown)="$event.stopPropagation(); saveEdit(rowData)"
                  ></button>
                  <button
                    pButton
                    icon="pi pi-times"
                    class="p-button-text p-button-sm p-button-danger p-0 action-btn"
                    (mousedown)="$event.stopPropagation(); cancelEdit()"
                  ></button>
                </div>
              } @else {
                <div class="name-wrapper">
                  <span class="name-text">{{
                    rowData.name_tag || "(no name)"
                  }}</span>

                  @if (!rowData.doi && !rowData.is_published) {
                    <button
                      pButton
                      icon="pi pi-pencil"
                      class="p-button-text p-button-sm p-0 edit-icon"
                      pTooltip="Rename"
                      (click)="$event.stopPropagation(); startEdit(rowData)"
                    ></button>
                  }
                </div>
              }
            </div>
          </td>

          <td class="datasets-reference-cell">
            @if (rowData.datasetsDisplay?.length) {
              @for (d of rowData.datasetsDisplay; track d) {
                <div class="dataset-line">
                  <strong>{{ d.dataset }}</strong>
                  <span class="muted">
                    — {{ d.version }}, {{ d.variable }}
                    @if (d.unit) {
                      <span>[{{ d.unit }}]</span>
                    }
                  </span>
                </div>
              }
            } @else {
              —
            }
          </td>

          <td class="spatial-reference-cell">
            @if (rowData.spatialReferenceDisplay) {
              <div class="dataset-line">
                <strong>{{ rowData.spatialReferenceDisplay.dataset }}</strong>
                <span class="muted">
                  — {{ rowData.spatialReferenceDisplay.version }}</span
                >
              </div>
            } @else {
              —
            }
          </td>

          <td class="settings-cell">
            <button
              pButton
              type="button"
              class="p-button-text p-button-secondary"
              icon="pi pi-replay"
              pTooltip="Load settings (redirects to validate page)"
              (click)="loadSettings(rowData, $event)"
            ></button>
          </td>

          <td class="status-cell">
            @let status = getStatusDisplay(rowData);
            <p-tag
              [severity]="status.severity"
              [value]="status.label"
              [style]="
                status.severity !== 'warn'
                  ? { background: 'transparent' }
                  : { 'white-space': 'pre-line' }
              "
            >
              @if (status.spinning) {
                <i
                  class="pi pi-spin pi-spinner mr-1"
                  style="font-size: 0.7rem"
                ></i>
              }
            </p-tag>
          </td>

          <td class="delete-cell">
            @if (isStoppable(rowData)) {
              <button
                pButton
                type="button"
                class="p-button-text p-button-sm p-button-danger"
                icon="pi pi-stop-circle"
                pTooltip="Stop validation"
                tooltipPosition="left"
                (click)="openStopDialog(rowData, $event)"
              ></button>
            } @else if (!isLive(rowData)) {
              @if (rowData.doi || rowData.is_published) {
                <p-tag severity="secondary" value="Published"></p-tag>
              } @else if (rowData.is_archived) {
                <p-tag severity="secondary" value="Archived"></p-tag>
              } @else {
                <button
                  pButton
                  type="button"
                  class="p-button-text p-button-danger"
                  icon="pi pi-trash"
                  pTooltip="Permanently delete"
                  tooltipPosition="left"
                  (click)="openDeleteDialog(rowData, $event)"
                ></button>
              }
            }
            <!-- isLive but not isStoppable = generating plots → show nothing -->
          </td>
        </tr>
      </ng-template>
    </p-table>

    @if (dataFetchError) {
      <div class="text-center w-12 my-2">
        <qa-error></qa-error>
      </div>
    }
  </div>

  <!-- p-dialog to delete validation-->
  <p-dialog
    header="Delete validation result"
    [(visible)]="confirmDeleteVisible"
    [modal]="true"
    [closable]="true"
    [dismissableMask]="true"
    [style]="{ width: '32rem' }"
    (onHide)="closeDeleteDialog()"
  >
    <div class="flex flex-column gap-2">
      <span>
        Do you really want to permanently delete
        <b>{{ deleteTarget?.name_tag || deleteTarget?.id }}</b
        >?
      </span>
    </div>

    <ng-template pTemplate="footer">
      <button
        pButton
        type="button"
        class="p-button-text"
        label="Cancel"
        (click)="closeDeleteDialog()"
        [disabled]="deleting"
      ></button>

      <button
        pButton
        type="button"
        class="p-button-text p-button-danger"
        label="Delete"
        icon="pi pi-trash"
        (click)="confirmDelete()"
        [loading]="deleting"
      ></button>
    </ng-template>
  </p-dialog>

  <!-- p-dialog to stop validation-->
  <p-dialog
    header="Stop validation"
    [(visible)]="confirmStopVisible"
    [modal]="true"
    [dismissableMask]="true"
    [style]="{ width: '32rem' }"
    (onHide)="closeStopDialog()"
  >
    <div class="flex flex-column gap-2">
      <span>
        Do you really want to stop
        <b>{{ stopTarget?.name_tag || stopTarget?.id }}</b
        >?
      </span>
      <span class="muted"
        >The validation run will be cancelled and no further processing will
        occur.</span
      >
    </div>

    <ng-template pTemplate="footer">
      <button
        pButton
        type="button"
        class="p-button-text"
        label="Cancel"
        (click)="closeStopDialog()"
        [disabled]="stopping"
      ></button>

      <button
        pButton
        type="button"
        class="p-button-text p-button-danger"
        label="Stop"
        icon="pi pi-stop-circle"
        (click)="confirmStop()"
        [loading]="stopping"
      ></button>
    </ng-template>
  </p-dialog>
  <!-- p-dialog for bulk actions -->
  <p-dialog
    [header]="bulkHeader"
    [(visible)]="bulkVisible"
    [modal]="true"
    [closable]="true"
    [dismissableMask]="true"
    [style]="{ width: '32rem' }"
    (onHide)="closeBulkDialog()"
  >
    <div class="flex flex-column gap-2">
      <span>
        Do you really want to {{ bulkLabel }}
        <b>{{ bulkTargets.length }}</b> validation(s)?
      </span>

      @if (bulkTargets.length < selectedRows.length) {
        <span class="muted">
          {{ selectedRows.length - bulkTargets.length }} of the selected rows will
          be skipped — this action does not apply to them.
        </span>
      }

      @if (bulkAction === 'delete') {
        <span class="muted">This cannot be undone.</span>
      }
    </div>

    <ng-template pTemplate="footer">
      <button pButton type="button" class="p-button-text" label="Cancel"
              (click)="closeBulkDialog()" [disabled]="bulkBusy"></button>
      <button pButton type="button" label="Confirm"
              [class]="bulkAction === 'delete'
                ? 'p-button-text p-button-danger'
                : 'p-button-text'"
              (click)="confirmBulk()" [loading]="bulkBusy"></button>
    </ng-template>
  </p-dialog>
</main>
